import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { motion, AnimatePresence } from "framer-motion";
import { MagnifyingGlass, ArrowRight } from "@phosphor-icons/react";
import { searchAll } from "@/lib/api";

const DEBOUNCE = 180;

export default function CommandPalette({ open, onOpenChange }) {
  const { t, i18n } = useTranslation();
  const lang = i18n.language || "en";
  const navigate = useNavigate();
  const [q, setQ] = useState("");
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [active, setActive] = useState(0);
  const inputRef = useRef(null);
  const timerRef = useRef(0);

  useEffect(() => {
    if (open) {
      setQ("");
      setResults([]);
      setActive(0);
      setTimeout(() => inputRef.current?.focus(), 30);
    }
  }, [open]);

  useEffect(() => {
    if (!open) return;
    if (!q.trim()) {
      setResults([]);
      return;
    }
    setLoading(true);
    clearTimeout(timerRef.current);
    timerRef.current = setTimeout(async () => {
      try {
        const data = await searchAll(q, lang);
        setResults(data.results || []);
        setActive(0);
      } finally {
        setLoading(false);
      }
    }, DEBOUNCE);
    return () => clearTimeout(timerRef.current);
  }, [q, lang, open]);

  const items = useMemo(() => results, [results]);

  const onKeyDown = (e) => {
    if (e.key === "Escape") onOpenChange(false);
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setActive((a) => Math.min(items.length - 1, a + 1));
    }
    if (e.key === "ArrowUp") {
      e.preventDefault();
      setActive((a) => Math.max(0, a - 1));
    }
    if (e.key === "Enter") {
      const it = items[active];
      if (it) {
        onOpenChange(false);
        navigate(it.url);
      }
    }
  };

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.25 }}
          className="fixed inset-0 z-[80] flex items-start justify-center bg-black/60 px-4 pt-[12vh] backdrop-blur-md"
          onClick={() => onOpenChange(false)}
          data-testid="cmdk-overlay"
        >
          <motion.div
            initial={{ y: -20, opacity: 0, scale: 0.98 }}
            animate={{ y: 0, opacity: 1, scale: 1 }}
            exit={{ y: -12, opacity: 0, scale: 0.98 }}
            transition={{ duration: 0.35, ease: [0.16, 1, 0.3, 1] }}
            onClick={(e) => e.stopPropagation()}
            className="w-full max-w-2xl overflow-hidden rounded-2xl glass-strong"
            style={{ boxShadow: "0 40px 100px rgba(0,0,0,0.6), 0 0 0 1px rgba(200,160,69,0.18)" }}
            data-testid="cmdk-panel"
            role="dialog"
            aria-label={t("nav.search")}
          >
            <div className="flex items-center gap-3 border-b border-white/[0.06] px-5 py-4">
              <MagnifyingGlass size={18} weight="regular" className="text-white/50" />
              <input
                ref={inputRef}
                value={q}
                onChange={(e) => setQ(e.target.value)}
                onKeyDown={onKeyDown}
                placeholder={t("search.placeholder")}
                className="flex-1 bg-transparent py-1 text-base text-white placeholder:text-white/40 focus:outline-none"
                data-testid="cmdk-input"
              />
              <kbd className="rounded border border-white/10 bg-black/40 px-2 py-1 font-mono text-[10px] text-white/60">ESC</kbd>
            </div>
            <div className="max-h-[54vh] overflow-y-auto py-2">
              {!q.trim() ? (
                <div className="px-6 py-10 text-center text-sm text-white/45">{t("search.empty")}</div>
              ) : loading ? (
                <div className="px-6 py-10 text-center text-sm text-white/45">…</div>
              ) : items.length === 0 ? (
                <div className="px-6 py-10 text-center text-sm text-white/45">{t("search.no_results")}</div>
              ) : (
                <ul>
                  {items.map((it, i) => (
                    <li key={`${it.url}-${i}`}>
                      <button
                        onClick={() => {
                          onOpenChange(false);
                          navigate(it.url);
                        }}
                        onMouseEnter={() => setActive(i)}
                        className={`group flex w-full items-center justify-between gap-4 px-5 py-3 text-left transition-colors ${
                          active === i ? "bg-bht-gold/10" : "hover:bg-white/[0.03]"
                        }`}
                        data-testid={`cmdk-result-${i}`}
                      >
                        <div className="flex flex-col">
                          <span className="text-[14.5px] text-white">{it.title}</span>
                          {it.excerpt && <span className="mt-0.5 line-clamp-1 text-xs text-white/50">{it.excerpt}</span>}
                        </div>
                        <div className="flex items-center gap-2 text-white/40">
                          <span className="rounded-full border border-white/10 px-2 py-0.5 font-mono text-[10px] uppercase tracking-widest">
                            {it.type}
                          </span>
                          <ArrowRight size={14} className={active === i ? "text-bht-gold" : ""} />
                        </div>
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </div>
            <div className="flex items-center justify-between border-t border-white/[0.06] px-5 py-2.5 text-[10px] uppercase tracking-widest text-white/40">
              <div className="flex items-center gap-3">
                <span><kbd className="rounded border border-white/10 bg-black/40 px-1.5 py-0.5 font-mono text-[10px]">↑↓</kbd> navigate</span>
                <span><kbd className="rounded border border-white/10 bg-black/40 px-1.5 py-0.5 font-mono text-[10px]">↵</kbd> open</span>
              </div>
              <span>bht enterprise search</span>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
